import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { teamService, Team, TeamMember } from '../../services/teamService';
import dayAwayService, { TimeOffEntry } from '../../services/dayAwayService';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { UserPlus, X } from 'lucide-react';
import { format, isBefore, startOfDay, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import '../../styles/calendar-dark-mode-fix.css';

interface TeamTimeOff {
  [date: string]: string[]; // date -> array of names
}

interface CalendarDay {
  date: string;
  employees: TimeOffEntry[];
}

const MyTeam: React.FC = () => {
  const { userEmail } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [employees, setEmployees] = useState<string[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [teamTimeOff, setTeamTimeOff] = useState<TeamTimeOff>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [allTimeOffData, setAllTimeOffData] = useState<CalendarDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!userEmail) {
        setLoading(false);
        return;
      }

      try {
        // Step 1: Load team data first
        const teamData = await teamService.getTeam(userEmail);
        setTeam(teamData);

        // Step 2: Load employee names lazily (only when dropdown is needed)
        const employeeList = await dayAwayService.getEmployeeNamesLazy();
        setEmployees(employeeList);

        // Step 3: Load ONLY team member time off data (OPTIMIZED)
        if (teamData && teamData.members.length > 0) {
          console.log('🚀 OPTIMIZED: Loading time off data for team members only');
          const teamMemberNames = teamData.members.map(member => member.name);
          const teamTimeOffEntries = await dayAwayService.getTeamTimeOffData(teamMemberNames);
          
          // Convert time off entries to calendar format
          const calendarData = convertTimeOffToCalendarData(teamTimeOffEntries);
          setAllTimeOffData(calendarData);
          updateTeamTimeOff(teamData, calendarData);
        } else {
          console.log('📭 No team members, skipping time off data loading');
          setAllTimeOffData([]);
          setTeamTimeOff({});
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userEmail]);

  // OPTIMIZED: Convert time off entries to calendar data format
  const convertTimeOffToCalendarData = (timeOffEntries: TimeOffEntry[]): CalendarDay[] => {
    const dateMap: { [date: string]: TimeOffEntry[] } = {};
    
    timeOffEntries.forEach(entry => {
      if (!dateMap[entry.date]) {
        dateMap[entry.date] = [];
      }
      dateMap[entry.date].push(entry);
    });

    return Object.entries(dateMap).map(([date, employees]) => ({
      date,
      employees
    }));
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setShowDialog(true);
    }
  };

  const updateTeamTimeOff = (teamData: Team, calendarData: CalendarDay[]) => {
    const timeOffMap: TeamTimeOff = {};
    calendarData.forEach((day: CalendarDay) => {
      const teamMembersOff = day.employees
        .filter((entry: TimeOffEntry) => teamData.members.some(member => member.name === entry.employee))
        .map((entry: TimeOffEntry) => entry.employee);
      
      if (teamMembersOff.length > 0) {
        timeOffMap[day.date] = teamMembersOff;
      }
    });
    setTeamTimeOff(timeOffMap);
  };

  const handleAddMember = async () => {
    if (!selectedEmployee || !userEmail) return;

    const newMember: TeamMember = {
      email: userEmail,
      name: selectedEmployee,
      dateAdded: new Date().toISOString()
    };

    try {
      await teamService.addTeamMember(userEmail, newMember);
      const updatedTeam = await teamService.getTeam(userEmail);
      setTeam(updatedTeam);
      setSelectedEmployee('');
      
      // OPTIMIZED: Load time off data for the updated team (including new member)
      if (updatedTeam && updatedTeam.members.length > 0) {
        console.log('🔄 OPTIMIZED: Reloading team time off data after adding member');
        const teamMemberNames = updatedTeam.members.map(member => member.name);
        const teamTimeOffEntries = await dayAwayService.getTeamTimeOffData(teamMemberNames);
        const calendarData = convertTimeOffToCalendarData(teamTimeOffEntries);
        setAllTimeOffData(calendarData);
        updateTeamTimeOff(updatedTeam, calendarData);
      }
    } catch (error) {
      console.error('Error adding team member:', error);
    }
  };

  const handleRemoveMember = async (memberName: string) => {
    if (!userEmail) return;

    try {
      const updatedMembers = team?.members.filter(m => m.name !== memberName) || [];
      const updatedTeam: Team = {
        members: updatedMembers,
        lastUpdated: new Date().toISOString()
      };
      await teamService.updateTeam(userEmail, updatedTeam);
      setTeam(updatedTeam);
      
      // OPTIMIZED: Reload time off data for remaining team members only
      if (updatedTeam.members.length > 0) {
        console.log('🔄 OPTIMIZED: Reloading team time off data after removing member');
        const teamMemberNames = updatedTeam.members.map(member => member.name);
        const teamTimeOffEntries = await dayAwayService.getTeamTimeOffData(teamMemberNames);
        const calendarData = convertTimeOffToCalendarData(teamTimeOffEntries);
        setAllTimeOffData(calendarData);
        updateTeamTimeOff(updatedTeam, calendarData);
      } else {
        console.log('📭 No team members remaining, clearing time off data');
        setAllTimeOffData([]);
        setTeamTimeOff({});
      }
    } catch (error) {
      console.error('Error removing team member:', error);
    }
  };

  if (!userEmail) {
    return <div className="p-4">Please log in to view your team.</div>;
  }

  if (loading) {
    return (
      <div className="container mx-auto p-4 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading team data...</p>
        </div>
      </div>
    );
  }

  const today = startOfDay(new Date());
  const futureTimeOff = Object.entries(teamTimeOff)
    .filter(([date]) => !isBefore(parseISO(date), today))
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">My Team</h1>
      
      {/* Single column layout like Calendar page */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Add Team Member */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle>Add Team Member</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees
                      .filter(emp => !team?.members.some(m => m.name === emp))
                      .map((emp, index) => (
                        <SelectItem key={`employee-${emp}-${index}`} value={emp}>
                          {emp}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleAddMember}>Add to Team</Button>
              </div>
            </CardContent>
          </Card>

          {/* Team Calendar */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle>Team Calendar</CardTitle>
            </CardHeader>
            <CardContent className="p-1">
              <div className="w-full h-[400px] sm:h-[500px] md:h-[600px] flex justify-center items-center overflow-hidden">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  className="origin-center dark:bg-gray-800 dark:text-gray-100 [&_.rdp-day]:dark:text-gray-100 [&_.rdp-day_button:hover]:dark:!bg-gray-600 [&_.rdp-day_button:hover]:dark:!text-black [&_.rdp-day_button:focus]:dark:!bg-gray-600 [&_.rdp-day_button:focus]:dark:!text-black [&_.rdp-day_button.rdp-day_selected]:dark:bg-cyan-600 [&_.rdp-day_button.rdp-day_selected]:dark:text-gray-100 [&_.rdp-table]:w-full [&_.rdp-month]:w-full [&_.rdp-caption]:w-full scale-[1.2] -translate-y-2 sm:scale-150 md:scale-[1.7] lg:scale-[1.9] xl:scale-[2.1]"
                  modifiers={{
                    teamOff: (date) => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      return !!teamTimeOff[dateStr];
                    }
                  }}
                  modifiersStyles={{
                    teamOff: { backgroundColor: 'rgba(0, 186, 199, 0.1)' }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Team Members List */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle>Team Members</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[200px] overflow-y-auto pr-2">
                {!team?.members.length ? (
                  <p className="text-muted-foreground">No team members added yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {team.members.map((member, index) => (
                      <li key={`member-${member.name}-${index}`} className="flex items-center justify-between p-2 bg-secondary rounded-lg">
                        <div className="flex items-center gap-2">
                          <UserPlus className="h-4 w-4" />
                          <span>{member.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMember(member.name)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Time Off */}
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Team Time Off</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[300px] md:max-h-[400px] overflow-y-auto">
              {futureTimeOff.map(([date, members]) => {
                const formattedDate = format(parseISO(date), 'MMM d, yyyy');
                return (
                  <div key={date} className="mb-2 text-sm">
                    <strong>{formattedDate}:</strong>{' '}
                    {members.join(', ')} {members.length > 1 ? 'are' : 'is'} off
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Selected Date Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white dark:bg-gray-800 border dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-gray-100">
              Team Members Off on {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedDate && teamTimeOff[format(selectedDate, 'yyyy-MM-dd')] ? (
              <ul className="space-y-2">
                {teamTimeOff[format(selectedDate, 'yyyy-MM-dd')].map((member, index) => (
                  <li key={`${member}-${index}`} className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <UserPlus className="h-4 w-4 text-cyan-500" />
                    {member}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">No team members are off on this day.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTeam;
